<template>
  <transition name="fade" mode="out-in">
    <div
      v-if="isModalOpen"
      class="w-screen h-screen fixed top-0 left-0 bg-primary-navy bg-opacity-80 z-chatbot-front md:px-3 flex justify-center items-center"
    >
      <div
        class="absolute top-0 left-0 w-full h-full cursor-pointer"
        @click="closeModal"
      ></div>

      <div
        class="w-full max-w-[980px] max-h-full pt-16 pb-13 md:px-24 relative bg-white rounded-lg"
      >
        <button
          type="button"
          class="w-10 h-10 absolute top-3 right-3"
          @click="closeModal"
        >
          <Sprite id="icon-close" classes="w-full h-full" />
        </button>

        <div
          v-if="!hasPostVideoContent || (hasPostVideoContent && !videoEnded)"
          class="md:aspect-w-16 md:aspect-h-9"
        >
          <div>
            <div
              class="plyr w-full h-full"
              ref="modalVideo"
              :data-plyr-provider="videoProvider"
              :data-plyr-embed-id="videoId"
            ></div>
          </div>
        </div>

        <div
          v-if="hasPostVideoContent && videoEnded"
          class="px-4 md:px-12 lg:px-30 text-center"
        >
          <slot name="critter"></slot>
          <p class="text-[2rem] leading-base font-bold text-heading mb-2">
            {{ postVideoHeading }}
          </p>
          <p class="u-body-lg text-heading mb-9">{{ postVideoContent }}</p>

          <div
            v-if="postVideoCtas.length > 0"
            class="flex flex-col lg:flex-row justify-center gap-y-3 lg:gap-y-0 lg:gap-x-3 mb-9"
          >
            <CtaButton
              v-for="ctaBlock in postVideoCtas"
              class="justify-center lg:justify-start"
              :key="ctaBlock.uid"
              :buttonType="ctaBlock.type"
              :label="ctaBlock.label"
              :url="ctaBlock.url"
              :clickFunction="e => ctaClickHandler(e, ctaBlock.url)"
            />
          </div>

          <CtaButton
            buttonType="third"
            tag="button"
            label="Replay video"
            iconId="icon-replay"
            :iconReversed="true"
            :clickFunction="replayVideo"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Plyr from 'plyr';

import EventBus from '../vue/bus';
import CtaButton from './CtaButton.vue';
import Sprite from './Sprite.vue';
import { scrollToTarget } from '../modules/ScrollTo';

import { lockScrolling, unlockScrolling } from '../helpers/browser';

export default {
  components: {
    CtaButton,
    Sprite,
  },
  props: {
    videoProvider: {
      type: String,
      required: true,
    },
    videoId: {
      type: String,
      required: true,
    },
    hasPostVideoContent: {
      type: Boolean,
      default: false,
    },
    postVideoHeading: {
      type: String,
    },
    postVideoContent: {
      type: String,
    },
    postVideoCtas: {
      type: Array,
    },
  },
  data() {
    return {
      isModalOpen: false,
      modalPlyr: null,
      ready: false,
      videoEnded: false,
    };
  },
  mounted() {
    this.initBus();
  },
  beforeDestroy() {
    this.destroyBus();
  },
  methods: {
    initBus() {
      EventBus.$on(EventBus.HERO_VIDEO_CLICK, () => {
        this.openModal();
      });
    },
    destroyBus() {
      EventBus.$off(EventBus.HERO_VIDEO_CLICK);
    },
    openModal() {
      lockScrolling();

      this.isModalOpen = true;

      this.$nextTick(() => {
        if (!this.plyrInited) {
          this.initVideo();
        }
      });
    },
    closeModal() {
      if (this.modalPlyr !== null) this.modalPlyr.destroy();
      this.modalPlyr = null;
      this.ready = false;
      this.videoEnded = false;

      this.isModalOpen = false;

      unlockScrolling();
    },
    initVideo() {
      if (this.modalPlyr === null) {
        this.modalPlyr = new Plyr(this.$refs.modalVideo, {
          settings: [],
          autoplay: true,
          autopause: false,
          muted: false,
          storage: {
            enabled: false,
          },
        });

        if (this.hasPostVideoContent) {
          this.modalPlyr.on('ended', () => {
            this.videoEndHandler();
          });
        }

        this.ready = true;
      }
    },
    videoEndHandler() {
      if (this.modalPlyr !== null) this.modalPlyr.destroy();
      this.modalPlyr = null;
      this.ready = false;

      this.$nextTick(() => {
        this.videoEnded = true;
      });
    },
    replayVideo() {
      this.videoEnded = false;

      this.$nextTick(() => {
        this.initVideo();
      });
    },
    ctaClickHandler(e, url) {
      if (url[0] === '#') {
        if (e?.preventDefault) e.preventDefault();
        this.closeModal();
        setTimeout(() => {
          scrollToTarget(url.replace('#', ''));
        }, 400);
      }
    },
  },
};
</script>

<style lang="scss">
.plyr {
  --plyr-color-main: #ed8b00;
}
</style>
